<template>
    <div ref="printArea">
        <PatientProfile
            :patientId    = "patientInfo.id"
            :name         = "patientInfo.name"
            :birth        = "patientInfo.birth"
            :age          = "patientInfo.age"
            :weight       = "patientInfo.weight"
            :lastExam     = "patientInfo.lastExam"
            :painArea     = "patientInfo.painArea"
            :showPainArea = "true"
            :chageName    = "true"
        />
        <hr style="border:0; border-top:1.5px solid #c8c8c8; margin:12px 0;" />
        <div class="pain-result-management">      
            <div class="result-section">
                <div class="diagnosis-header">
                    <div class="print-btn-container">
                        <button class="search-btn" @click="downloadPdf">PDF</button>
                    </div>
                </div>
                <div class="diagnosis-container">
                    <div class="top-row">
                        <div class="left-panel">
                            <div class="cpt-title">통증 부위</div>
                            <div class="body-card">
                                <!-- 인체도 + 통증 마커 -->
                                <div class="body-diagram">
                                    <img src="@/assets/resource/icon/body_shape.png" alt="인체도" class="pain-body-img"/>
                                    <div
                                        v-for="idx in getPainMarkerIndexes(diagnosis.location)"
                                        :key="idx"
                                        class="pain-area-marker"
                                        :style="getPainAreaMarkerStyle(idx)"
                                    ></div>
                                </div>
                                <div class="metrics-pain-area">
                                    <span class="metric-value">: {{ diagnosis.location }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="right-panel">
                            <div class="cpt-title">VAS(통증 정도)</div>
                            <div class="result-vas-section">
                                <div class="result-vas-icons">
                                    <div class="result-vas-grid">
                                        <span
                                            v-for="obj in vasFaces"
                                            :key="obj.score"
                                            :class="[
                                                'result-vas-emoji',
                                                { active: obj.score === diagnosis.vas }
                                            ]"
                                        >
                                            <img
                                                :src="require(`@/assets/resource/icon/${obj.fileName}.png`)"
                                                alt="vas score"
                                            />
                                            <div class="result-vas-score">{{ obj.score }}</div>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="cpt-title cptPec-title">C.P.T / P.E.C</div>
                            <div class="result-area">
                                <!-- 진단 수치 표시 박스 -->
                                <div class="metrics-card">
                                    <div class="metrics-grid">
                                        <div>
                                            <span class="metric">C.P.T</span>
                                            <span class="metric-value">{{ diagnosis.cpt }} μA</span>
                                        </div>
                                        <div>
                                            <span class="metric">P.E.C</span>
                                            <span class="metric-value">{{ diagnosis.pec }} μA</span>
                                        </div>
                                        <div>
                                            <span class="metric">SCORE</span>
                                            <span class="metric-value">{{ diagnosis.score }}</span>
                                        </div>
                                        <div>
                                            <span class="metric">VAS</span>
                                            <span class="metric-value">{{ diagnosis.vas }}/100</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="chart-row chart-row-col">
                                    <div class="bars">
                                        <!-- C.P.T -->
                                        <div
                                            class="bar-row cpt-row"
                                            :style="{ '--p': getPercent(diagnosis.cpt, CPT_MAX) + '%' }"
                                        >
                                            <div class="bar-bg cpt-bg"></div>
                                            <div class="cpt-bar" :style="{ width: getPercent(diagnosis.cpt, CPT_MAX) + '%' }"></div>
                                            <div class="bar-value">
                                                <span class="arrow">&#9650;</span>
                                                <div>{{ diagnosis.cpt }}</div>
                                            </div>
                                            <div class="bar-label">C.P.T</div>
                                            <div class="bar-max">{{ diagnosis.cpt }}μA / 256μA</div>
                                        </div>
                                        <!-- P.E.C -->
                                        <div
                                            class="bar-row pec-row"
                                            :style="{ '--p': getPercent(diagnosis.pec, PEC_MAX) + '%' }"
                                        >
                                            <div class="bar-bg pec-bg"></div>
                                            <div class="pec-bar" :style="{ width: getPercent(diagnosis.pec, PEC_MAX) + '%' }"></div>
                                            <div class="bar-value">
                                                <span class="arrow">&#9650;</span>
                                                <div>{{ diagnosis.pec }}</div>
                                            </div>
                                            <div class="bar-label">P.E.C</div>
                                            <div class="bar-max">{{ diagnosis.pec }}μA / 256μA</div>
                                        </div>
                                        <!-- SCORE -->
                                        <div
                                            class="bar-row score-row"
                                            :style="{ '--p': getPercent(Math.max(0, diagnosis.score), SCORE_MAX) + '%' }"
                                        >
                                            <div class="bar-bg score-bg"></div>
                                            <div class="score-bar" :style="{ width: getPercent(Math.max(0, diagnosis.score), SCORE_MAX) + '%' }"></div>
                                            <div class="bar-value">
                                                <span class="arrow">&#9650;</span>
                                                <div>{{ diagnosis.score }}</div>
                                            </div>
                                            <div class="bar-label">SCORE</div>
                                            <div class="bar-max">{{ diagnosis.score }} / 1000</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bottom-row">
                        <div class="chart-card">
                            <canvas ref="chartCanvas"></canvas>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
    import html2canvas from "html2canvas";
    import jsPDF       from "jspdf";

    defineOptions({
        meta: {
            title: '통증 프로파일',
            segmentKey: 'patientId'
        }
    })
    import PatientProfile                       from "@/components/common/PatientProfile.vue";
    import {onMounted, ref, watch, computed }   from "vue";
    import {useRoute}                           from "vue-router";
    import api, { urls }                        from "@/api";
    import Chart                                from "chart.js/auto";
    import ChartDataLabels                      from "chartjs-plugin-datalabels";
    Chart.register(ChartDataLabels);

    import { commonUtils } from "@/utils/commonUtils";
    import { useAppAlert }  from '@/composables/useAppAlert'


    const { showAlert, confirm } = useAppAlert()

    const route         = useRoute()
    const patientInfo   = ref({})
    const diagnosis     = ref({})

    const chartCanvas   = ref(null);
    let chartInstance   = null;
    const printArea     = ref(null);

    const CPT_MAX       = 256;     // 화면에 표시하는 최대값과 동일하게
    const PEC_MAX       = 256;
    const SCORE_MAX     = 1000;

    const vasFaces = [
        { score: 10,  fileName: "vas_score_10"  },
        { score: 20,  fileName: "vas_score_20"  },
        { score: 30,  fileName: "vas_score_30"  },
        { score: 40,  fileName: "vas_score_40"  },
        { score: 50,  fileName: "vas_score_50"  },
        { score: 60,  fileName: "vas_score_60"  },
        { score: 70,  fileName: "vas_score_70"  },
        { score: 80,  fileName: "vas_score_80"  },
        { score: 90,  fileName: "vas_score_90"  },
        { score: 100, fileName: "vas_score_100" }
    ]

    const downloadPdf = async () => {
        try {
            const element = printArea.value;
            if (!element) {
                showAlert("캡처할 영역을 찾을 수 없습니다.");
                return;
            }

            // 1. element 클론 생성
            const cloned = element.cloneNode(true);

            // 2. PDF용 고정 스타일 적용
            cloned.classList.add("pdf-mode");
            cloned.style.position   = "absolute";
            cloned.style.top        = "0";
            cloned.style.left       = "0";
            cloned.style.zIndex     = "-9999";
            cloned.style.background = "#fff";
            document.body.appendChild(cloned);

            if (chartCanvas.value && chartInstance) {
                const chartImage = chartInstance.toBase64Image(); // Chart.js 이미지 추출
                const clonedCanvas = cloned.querySelector('canvas');
                if (clonedCanvas) {
                    const img = document.createElement('img');
                    img.src = chartImage;
                    img.style.width = clonedCanvas.style.width || '400px';
                    img.style.height = clonedCanvas.style.height || '250px';
                    clonedCanvas.replaceWith(img); // canvas를 img로 교체
                }
            }

            // 3. 렌더링 안정화 대기 (100~200ms)
            await new Promise(res => setTimeout(res, 250));

            // 4. html2canvas로 캡처
            const canvas = await html2canvas(cloned, {
                scale        : 1,
                useCORS      : true,
                scrollX      : 0,
                scrollY      : 0,
                windowWidth  : cloned.scrollWidth,
                windowHeight : cloned.scrollHeight,
            });

            document.body.removeChild(cloned);

            // 5. PDF 생성
            const imgData   = canvas.toDataURL("image/jpeg", 1.0);
            const pdfWidth  = 794; // A4 landscape
            const pdfHeight = 1123;
            const imgWidth  = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const pdf       = new jsPDF("p", "px", [pdfWidth, pdfHeight]);

            let heightLeft  = imgHeight;
            let position    = 0;

            while (heightLeft > 0) {
                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;
                if (heightLeft > 0) {
                    pdf.addPage();
                    position -= pdfHeight;
                }
            }

            const fileName = `통증진단_${patientInfo.value?.name || "무명"}_${new Date().toISOString().slice(0,10)}.pdf`;
            // Base64 데이터 URL 생성 + 파일명 포함
            const pdfDataUri = pdf.output('dataurlstring');
            
            // 파일명을 data URL에 포함
            const dataUriWithFilename = pdfDataUri.replace(
                'data:application/pdf',
                `data:application/pdf;filename=${encodeURIComponent(fileName)}`
            );
            
            const link = document.createElement('a');
            link.href       = dataUriWithFilename;
            link.download   = fileName;
            link.click();
        } catch (err) {
            showAlert("PDF 생성 중 오류가 발생했습니다.");
        }
    };

    async function PatientData(id, diagnosisId) {
        try {
            const params = {
                id: id,
                diagnosisId: diagnosisId
            };

            const res  = await api.post(urls.patientList.patientData, params);
            const data = res.data.list ? res.data.list[0] : res.data;

            // [1] 환자 프로필용 데이터
            patientInfo.value = {
                id          : data.patient_id,
                name        : data.name,
                birth       : data.dob || '',
                age         : commonUtils.calcAge(data.dob),
                weight      : data.weight,
                lastExam    : data.created_at ? data.created_at.split('T')[0] : '',
                painArea    : data.pain_area || '',
                diseaseName : data.disease_name || '',
                comment     : data.comment || ''
            };
            // [2] 진단 데이터 (템플릿 metrics 부분에서 사용)
            diagnosis.value = {
                cpt         : data.cpt_mean || 0,
                pec         : data.pec_mean || 0,
                score       : data.score || 0,
                vas         : data.vas_score || 0,
                location    : data.pain_area || '',
                locations   : data.pain_area
                    ? data.pain_area.split(',').map(s => s.trim())
                    : [],
                comment  : data.comment || '',
                cpt_time : data.cpt_time || 0,
                pec_time : data.pec_time || 0,
                set_time : data.set_time || 30,
            };

        } catch (err) {
            showAlert("환자 정보 불러오는데 실패했습니다.");
        }
    }

    const baseFrontRaw = [
        //앞쪽
        { cx: 73.5, cy: 43  },   // 목
        { cx: 47,   cy: 67  },   // 좌측 어깨
        { cx: 38.5, cy: 102 },   // 좌측 상완
        { cx: 35.5, cy: 139 },   // 좌측 팔꿈치
        { cx: 24,   cy: 179 },   // 좌측 손
        { cx: 52,   cy: 163 },   // 좌측 골반
        { cx: 58,   cy: 213 },   // 좌측 허벅지
        { cx: 59,   cy: 280 },   // 좌측 종아리
        { cx: 64,   cy: 329 },   // 좌측 발

        { cx: 100, cy: 67  },   // 우측 어깨
        { cx: 109, cy: 102 },   // 우측 상완
        { cx: 111, cy: 139 },   // 우측 팔꿈치
        { cx: 124, cy: 179 },   // 우측 손
        { cx: 95,  cy: 163 },   // 우측 골반
        { cx: 88,  cy: 213 },   // 우측 허벅지
        { cx: 88,  cy: 280 },   // 우측 종아리
        { cx: 83,  cy: 329 },   // 우측 발

        { cx: 73.5, cy: 99    },   // 가슴 중앙
        { cx: 73.5, cy: 128.5 },   // 배

        //뒤쪽
        { cx: 227.5, cy: 43  },   // 목
        { cx: 201,   cy: 67  },   // 좌측 어깨
        { cx: 192.5, cy: 102 },   // 좌측 상완
        { cx: 190,   cy: 139 },   // 좌측 팔꿈치
        { cx: 177,   cy: 179 },   // 좌측 손
        { cx: 206,   cy: 163 },   // 좌측 골반
        { cx: 212,   cy: 213 },   // 좌측 허벅지
        { cx: 214,   cy: 280 },   // 좌측 종아리
        { cx: 218.5, cy: 329 },   // 좌측 발

        { cx: 255, cy: 67  },   // 우측 어깨
        { cx: 263, cy: 102 },   // 우측 상완
        { cx: 265, cy: 139 },   // 우측 팔꿈치
        { cx: 278, cy: 179 },   // 우측 손
        { cx: 249, cy: 163 },   // 우측 골반
        { cx: 243, cy: 213 },   // 우측 허벅지
        { cx: 242, cy: 280 },   // 우측 종아리
        { cx: 237, cy: 329 },   // 우측 발

        { cx: 227.5, cy: 99    },   // 가슴 중앙
        { cx: 227.5, cy: 128.5 },   // 배

    ];

    const frontAreaLabels = [
        // 앞쪽
        '목 (전면)',
        '우측 어깨 (전면)',
        '우측 상완 (전면) ',
        '우측 전완 (전면)',
        '우측 손바닥 (전면)',
        '좌측 골반 (전면)',
        '우측 허벅지 (전면)',
        '우측 정강이 (전면)',
        '우측 발 (전면)',

        '좌측 어깨 (전면)',
        '좌측 상완 (전면)',
        '좌측 전완 (전면)',
        '좌측 손바닥 (전면)',
        '좌측 골반 (전면)',
        '좌측 허벅지 (전면)',
        '좌측 정강이 (전면)',
        '좌측 발 (전면)',

        '가슴 (전면)',
        '배 (전면)',

        // 뒤쪽
        '목 (후면)',
        '좌측 어깨 (후면)',
        '좌측 상완 (후면)',
        '좌측 전완 (후면)',
        '좌측 손등 (후면)',
        '좌측 엉덩이 (후면)',
        '좌측 허벅지 (후면)',
        '좌측 종아리 (후면)',
        '좌측 발 (후면)',

        '우측 어깨 (후면)',
        '우측 상완 (후면)',
        '우측 전완 (후면)',
        '우측 손등 (후면)',
        '우측 엉덩이 (후면)',
        '우측 허벅지 (후면)',
        '우측 종아리 (후면)',
        '우측 발 (후면)',

        '등 (후면)',
        '허리 (후면)'
    ];


    const PAIR_GROUPS = [
        // {좌측, 우측}
        //목
        { groupName: '목 전후면', parts: [0, 19] },
        //어깨
        { groupName: '양측 어깨 전면', parts: [9, 1] },
        { groupName: '양측 어깨 후면', parts: [20, 28] },
        { groupName: '좌측 어깨 전후면', parts: [9, 20] },
        { groupName: '우측 어깨 전후면', parts: [1, 28] },
        { groupName: '양측 어깨 전후면', parts: [9, 1, 20, 28] },
        //상완
        { groupName: '양측 상완 전면', parts: [10, 2] },
        { groupName: '양측 상완 후면', parts: [21, 29] },
        { groupName: '좌측 상완 전후면', parts: [10, 21] },
        { groupName: '우측 상완 전후면', parts: [2, 29] },
        { groupName: '양측 상완 전후면', parts: [10, 2, 21, 29] },
        //전완
        { groupName: '양측 전완 전면', parts: [11, 3] },
        { groupName: '양측 전완 후면', parts: [22, 30] },
        { groupName: '좌측 전완 전후면', parts: [11, 22] },
        { groupName: '우측 전완 전후면', parts: [3, 30] },
        { groupName: '양측 전완 전후면', parts: [11, 3, 22, 30] },
        //손바닥
        { groupName: '양측 손바닥', parts: [12, 4] },
        //손등
        { groupName: '양측 손등', parts: [23, 31] },
        //손
        { groupName: '좌측 손', parts: [12, 23] },
        { groupName: '우측 손', parts: [4, 31] },
        { groupName: '양측 손', parts: [12, 4, 23, 31] },
        //골반
        { groupName: '양측 골반', parts: [13, 5] },
        //엉덩이
        { groupName: '양측 엉덩이', parts: [24, 32] },
        //허벅지
        { groupName: '양측 허벅지 전면', parts: [14, 6] },
        { groupName: '양측 허벅지 후면', parts: [25, 33] },
        { groupName: '좌측 허벅지 전후면', parts: [14, 25] },
        { groupName: '우측 허벅지 전후면', parts: [6, 33] },
        { groupName: '양측 허벅지 전후면', parts: [14, 6, 25, 33] },
        //정강이
        { groupName: '양측 정강이', parts: [15, 7] },
        //종아리
        { groupName: '양측 종아리', parts: [26, 34] },
        //발
        { groupName: '양측 발 전면', parts: [16, 8] },
        { groupName: '양측 발 후면', parts: [27, 35] },
        { groupName: '좌측 발 전후면', parts: [16, 27] },
        { groupName: '우측 발 전후면', parts: [8, 35] },
        { groupName: '양측 발 전후면', parts: [16, 8, 27, 35] },

        //상체
        {
            groupName: '상체 전면',   //목, 어깨, 상완, 전완, 손바닥, 가슴, 배
            parts:
                [
                    0,          //목
                    9, 1,       //어깨(좌,우)
                    10, 2,      //상완(좌,우)
                    11, 3,      //전완(좌,우)
                    12, 4,      //손바닥(좌,우)
                    17,         //가슴
                    18          //배
                ]
        },
        {
            groupName: '상체 후면',   //목, 어깨, 상완, 전완, 손등, 등, 허리
            parts:
                [
                    19,          //목
                    20, 28,     //어깨(좌,우)
                    21, 29,     //상완(좌,우)
                    22, 30,     //전완(좌,우)
                    23,31,      //손등(좌,우)
                    36,         //등
                    37          //허리
                ]
        },
        {
            groupName: '상체 전반',   //목, 어깨, 상완, 전완, 손, 가슴, 배
            parts:
                [
                    //전면
                    0,          //목
                    9, 1,       //어깨(좌,우)
                    10, 2,      //상완(좌,우)
                    11, 3,      //전완(좌,우)
                    12, 4,      //손바닥(좌,우)
                    17,         //가슴
                    18,         //배
                    //후면
                    19,          //목
                    20, 28,     //어깨(좌,우)
                    21, 29,     //상완(좌,우)
                    22, 30,     //전완(좌,우)
                    23,31,      //손등(좌,우)
                    36,         //등
                    37          //허리
                ]
        },
        //하체
        {
            groupName: '하체 전면',   //골반, 허벅지, 정강이, 발
            parts:
                [
                    13, 5,      //골반(좌,우)
                    14, 6,      //허벅지(좌,우)
                    15, 7,      //정강이(좌,우)
                    16, 8,      //발(좌,우)
                ]
        },
        {
            groupName: '하체 후면',   //엉덩이, 허벅지, 종아리, 발
            parts:
                [
                    24, 32,      //엉덩이(좌,우)
                    25, 33,      //허벅지(좌,우)
                    26, 34,      //종아리(좌,우)
                    27, 35,      //발(좌,우)
                ]
        },
        {
            groupName: '하체 전반',   //골반, 엉덩이, 허벅지, 정강이, 종아리, 발
            parts:
                [
                    //전면
                    13, 5,      //골반(좌,우)
                    14, 6,      //허벅지(좌,우)
                    15, 7,      //정강이(좌,우)
                    16, 8,      //발(좌,우)
                    //후면
                    24, 32,      //엉덩이(좌,우)
                    25, 33,      //허벅지(좌,우)
                    26, 34,      //종아리(좌,우)
                    27, 35,      //발(좌,우)
                ]
        },
        {
            groupName: '몸 전체',
            parts:
                [
                    //상체 전면
                    0,          //목
                    9, 1,       //어깨(좌,우)
                    10, 2,      //상완(좌,우)
                    11, 3,      //전완(좌,우)
                    12, 4,      //손바닥(좌,우)
                    17,         //가슴
                    18,         //배
                    //후면
                    19,          //목
                    20, 28,     //어깨(좌,우)
                    21, 29,     //상완(좌,우)
                    22, 30,     //전완(좌,우)
                    23,31,      //손등(좌,우)
                    36,         //등
                    37,          //허리

                    //하체 전면
                    13, 5,      //골반(좌,우)
                    14, 6,      //허벅지(좌,우)
                    15, 7,      //정강이(좌,우)
                    16, 8,      //발(좌,우)
                    //후면
                    24, 32,      //엉덩이(좌,우)
                    25, 33,      //허벅지(좌,우)
                    26, 34,      //종아리(좌,우)
                    27, 35,      //발(좌,우)
                ]
        },
        // { groupName: '양측 손등 (후면)', parts: ['좌측 손등 (후면)', '우측 손등 (후면)'] },
    ];

    // 상수 객체로 분리
    const DIAGNOSIS_CONST = {
        cptCenter : 2000,  //C.P.T 값의 중앙 기준값
        cptRange  : 2500,  //C.P.T 값의 전체 범위
        pecCenter : 2200,  //P.E.C 값의 중앙 기준값
        pecRange  : 2500,  //P.E.C 값의 전체 범위
        cx        : 220,   //그래프의 x축 중심 좌표
        cy        : 100,   //그래프의 y축 중심 좌표
        rangeX    : 80,    //그래프의 x축 이동할 최대 범위
        rangeY    : 80     //그래프의 y축 이동할 최대 범위
    }

    const cptNorm = computed(() => {
        return Math.min(Math.max(((diagnosis.value.cpt || 0) - DIAGNOSIS_CONST.cptCenter) / DIAGNOSIS_CONST.cptRange, -1), 1);
    });

    const pecNorm = computed(() => {
        return Math.min(Math.max(((diagnosis.value.pec || 0) - DIAGNOSIS_CONST.pecCenter) / DIAGNOSIS_CONST.pecRange, -1), 1);
    });

    const polygonPoints       = computed(() => computePolygonPoints(diagnosis.value));
    const analysisDescription = computed(() => getAnalysisDescription(cptNorm.value, pecNorm.value));

    const frontAreas = baseFrontRaw.map(p => ({
        ...p,
        top: (p.cy / 338 * 100).toFixed(2) + '%',
        left: (p.cx / 301 * 100).toFixed(2) + '%'
    }));


    function getPainMarkerIndexes(painAreaString) {
        
        if (!painAreaString) return [];
        
        const items     = painAreaString.split(',').map(s => s.trim()).filter(Boolean);
        const indexes   = [];

        items.forEach(name => {
            // 개별 부위명칭일 경우
            if (frontAreaLabels.includes(name)) {
                const idx = frontAreaLabels.indexOf(name);
                if (idx !== -1) indexes.push(idx);
            }
            // 그룹명칭일 경우
            else {
                const group = PAIR_GROUPS.find(g => g.groupName === name);
                if (group) indexes.push(...group.parts);
            }
            // 이름이 둘다 아니면 오류(미처리)
        });
        const uniqueIndexes = Array.from(new Set(indexes));
        
        return uniqueIndexes;
    }

    function getPainAreaMarkerStyle(idx) {
        // idx: 반드시 인덱스여야 함!
        if (idx < 0 || idx >= frontAreas.length) {
            return { top: '100px', left: '150px' };
        }
        return {
            top: frontAreas[idx].top,
            left: frontAreas[idx].left
        };
    }

    // 차트 생성 함수
    function getChartConfig(diagnosisValue) {
        const yValues = [
            diagnosisValue.cpt,
            diagnosisValue.pec
        ];
        const yMaxRaw = Math.max(...yValues) + 100;
        const yMax    = Math.ceil(yMaxRaw / 100) * 100;

        return {
            type: 'scatter',
            data: {
                datasets: [
                    {
                        label: 'C.P.T',
                        data: [{ x: diagnosisValue.cpt_time, y: diagnosisValue.cpt }],
                        backgroundColor: '#fa6262',
                        pointRadius: 10,
                        pointHoverRadius: 18
                    },
                    {
                        label: 'P.E.C',
                        data: [{ x: diagnosisValue.pec_time, y: diagnosisValue.pec }],
                        backgroundColor: '#239cff',
                        pointRadius: 13,
                        pointHoverRadius: 18
                    }
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        title: { display: true, text: '(sec)', align: 'end' },
                        min: 0,
                        grid: { color: '#dde8f0' },
                        tick: { stepSize: 10 }
                    },
                    y: {
                        min: 0,
                        max: Math.max(CPT_MAX, PEC_MAX),
                        grid: { color: '#dde8f0' },
                        ticks: {
                            callback: (value) => `${value} μA`
                        }
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom'
                    },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: function (context) {
                                return `${context.dataset.label}: ${context.parsed.y}`;
                            }
                        }
                    },
                    datalabels: {
                        display: true,
                        align: 'top',           // 라벨 위치 조정 가능 ('top', 'center', 'bottom' 등)
                        anchor: 'end',          // 라벨 앵커 위치
                        font: {
                            weight: 'bold',
                            size: 12
                        },
                        formatter: function(value, context) {
                            return value.y;     // y값 출력
                        },
                        color: '#000'          // 글씨 색상 지정
                    }
                }
            }
        };
    }

    // 차트 생성 및 업데이트 함수
    function updateChart() {
        if (chartInstance) {
            chartInstance.destroy();
        }
        chartInstance = new Chart(chartCanvas.value, getChartConfig(diagnosis.value));
    }

    function computePolygonPoints(diagnosis) {
        const cpt = ((diagnosis.cpt || 0) - DIAGNOSIS_CONST.cptCenter) / DIAGNOSIS_CONST.cptRange;
        const pec = ((diagnosis.pec || 0) - DIAGNOSIS_CONST.pecCenter) / DIAGNOSIS_CONST.pecRange;

        // -1 ~ 1 제한
        const cptNorm = Math.min(Math.max(cpt, -1), 1);
        const pecNorm = Math.min(Math.max(pec, -1), 1);

        const cx     = DIAGNOSIS_CONST.cx;
        const cy     = DIAGNOSIS_CONST.cy;
        const rangeX = DIAGNOSIS_CONST.rangeX;
        const rangeY = DIAGNOSIS_CONST.rangeY;

        let points;

        if (cptNorm < 0 && pecNorm > 0) {
            // 좌상단 삼각형
            points = [
                `${cx},${cy}`,                      // 중심
                `${cx + cptNorm * rangeX},${cy}`,  // 좌측 (X 감소)
                `${cx},${cy - pecNorm * rangeY}`   // 상단 (Y 감소)
            ];
        } else if (cptNorm > 0 && pecNorm > 0) {
            // 우상단 삼각형
            points = [
                `${cx},${cy}`,
                `${cx + cptNorm * rangeX},${cy}`,  // 우측 (X 증가)
                `${cx},${cy - pecNorm * rangeY}`
            ];
        } else if (cptNorm < 0 && pecNorm < 0) {
            // 좌하단 삼각형
            points = [
                `${cx},${cy}`,
                `${cx + cptNorm * rangeX},${cy}`,  // 좌측 (X 감소)
                `${cx},${cy - pecNorm * rangeY}`   // 하단 (Y 증가)
            ];
        } else if (cptNorm > 0 && pecNorm < 0) {
            // 우하단 삼각형
            points = [
                `${cx},${cy}`,
                `${cx + cptNorm * rangeX},${cy}`,  // 우측
                `${cx},${cy - pecNorm * rangeY}`   // 하단
            ];
        } else {
            // 중점만 표시 (또는 기본 삼각형)
            points = [
                `${cx},${cy}`,
                `${cx},${cy - rangeY}`,
                `${cx + rangeX},${cy}`
            ];
        }

        return points.join(' ');
    }

    function getAnalysisDescription(cptNorm, pecNorm) {
        // 사분면:
        // 좌상단: cptNorm < 0, pecNorm > 0
        // 우상단: cptNorm > 0, pecNorm > 0
        // 좌하단: cptNorm < 0, pecNorm < 0
        // 우하단: cptNorm > 0, pecNorm < 0

        if (cptNorm < 0 && pecNorm > 0) {
            return [
                "감각과 통증 모두 과민한 상태로, 신경계 과활성 가능성이 있습니다.",
                "작은 자극에도 민감하여 중추 감작이 의심됩니다."
            ];
        } else if (cptNorm > 0 && pecNorm > 0) {
            return [
                "감각은 둔감하지만 통증에는 예민하여 신경병증성 통증이 의심됩니다.",
                "감각 반응 저하와 통증 과민이 동반되는 양상입니다."
            ];
        } else if (cptNorm < 0 && pecNorm < 0) {
            return [
                "감각은 민감하나 통증에는 둔감하여 말초 신경 이상 가능성이 있습니다.",
                "이상감각을 동반할 수 있는 상태로 보입니다."
            ];
        } else if (cptNorm > 0 && pecNorm < 0) {
            return [
                "감각과 통증 모두 둔감하여 말초 신경 손상 가능성이 큽니다.",
                "전반적인 감각 저하 상태로 신경 전도 이상을 의심합니다."
            ];
        } else {
            return [];
        }
    }

    function clamp(n, min, max) {
        return Math.min(Math.max(n, min), max);
    }

    function getPercent(value, max) {
        if (max <= 0) return 0;
        const p = (Number(value || 0) / max) * 100;
        return clamp(p, 0, 100);
    }


    // 진단 데이터가 변경될 때마다 차트 업데이트
    watch(diagnosis, (newVal) => {
        if (chartCanvas.value && newVal && newVal.cpt != null && newVal.pec != null) {
            updateChart();
        }
    }, { immediate: true });

    onMounted(() => {
        const idFromRoute = route.params.patientId;
        const diagnosisId = route.query.diagnosisId;
        PatientData(idFromRoute,diagnosisId);  // 환자 프로필 세팅
    })
</script>
<style>
    @media print {

        @page {
            size: A4;
            margin: 20mm;
        }

        body, html, #app {
            width: 100%;
            height: auto;
            padding: 20mm 15mm;
        }

        .diagnosis-main-container {
            background: white;
        }

        button, .no-print {
            display: none !important;
        }

        body {
            font-size: 12pt;
        }
    }
</style>